import type { RealMachinesDatabase } from '@/db/database'
import { getDatabase } from '@/db/database'
import { DatabaseOperationError, executeDatabaseOperation } from '@/db/errors'
import { normalizePhone, normalizeSearchText, toActiveKey } from '@/db/normalization'
import { clientSchema, createClientInputSchema, updateClientInputSchema } from '@/db/schemas'
import { createUuid } from '@/db/uuid'
import type { Client, CreateClientInput, UpdateClientInput } from '@/db/types'
import { changedFields, nextCode, requireRecord, writeAuditLog } from './helpers'

export class ClientRepository {
  constructor(private readonly database: RealMachinesDatabase = getDatabase()) {}

  async create(input: CreateClientInput): Promise<Client> {
    return executeDatabaseOperation('criar cliente', async () => {
      const data = createClientInputSchema.parse(input)
      return this.database.transaction(
        'rw',
        [this.database.clients, this.database.counters, this.database.auditLogs],
        async () => {
          const now = new Date().toISOString()
          const client = clientSchema.parse({
            ...data,
            id: createUuid(),
            code: await nextCode(this.database, 'clientCode', now),
            normalizedCompanyName: normalizeSearchText(data.companyName),
            normalizedFarmOrSite: normalizeSearchText(data.farmOrSite),
            normalizedPhone: normalizePhone(data.phone),
            active: true,
            activeKey: 1,
            createdAt: now,
            updatedAt: now,
          })
          await this.database.clients.add(client)
          await writeAuditLog(
            this.database,
            'client',
            client.id,
            'create',
            { code: client.code },
            now,
          )
          return client
        },
      )
    })
  }

  async getById(id: string): Promise<Client | undefined> {
    return executeDatabaseOperation('consultar cliente', async () => {
      const client = await this.database.clients.get(id)
      return client ? clientSchema.parse(client) : undefined
    })
  }

  async getByCode(code: string): Promise<Client | undefined> {
    return executeDatabaseOperation('consultar cliente por código', async () => {
      const client = await this.database.clients.where('code').equals(code).first()
      return client ? clientSchema.parse(client) : undefined
    })
  }

  async list(
    options: { activeOnly?: boolean; active?: boolean; limit?: number; offset?: number } = {},
  ): Promise<Client[]> {
    return executeDatabaseOperation('listar clientes', async () => {
      const limit = Math.min(Math.max(options.limit ?? 50, 1), 100)
      const offset = Math.max(options.offset ?? 0, 0)
      const active = options.active ?? ((options.activeOnly ?? true) ? true : undefined)
      const records =
        active !== undefined
          ? await this.database.clients
              .where('activeKey')
              .equals(active ? 1 : 0)
              .offset(offset)
              .limit(limit)
              .toArray()
          : await this.database.clients
              .orderBy('updatedAt')
              .reverse()
              .offset(offset)
              .limit(limit)
              .toArray()
      return records.map((record) => clientSchema.parse(record))
    })
  }

  async search(
    query: string,
    options: number | { limit?: number; includeInactive?: boolean } = 30,
  ): Promise<Client[]> {
    return executeDatabaseOperation('pesquisar clientes', async () => {
      const normalizedText = normalizeSearchText(query)
      const normalizedDigits = normalizePhone(query)
      if (!normalizedText && !normalizedDigits) return []

      const pageSize = Math.min(
        Math.max(typeof options === 'number' ? options : (options.limit ?? 30), 1),
        500,
      )
      const includeInactive =
        typeof options === 'number' ? false : (options.includeInactive ?? false)
      const searches = [
        this.database.clients
          .where('normalizedCompanyName')
          .startsWith(normalizedText)
          .limit(pageSize)
          .toArray(),
        this.database.clients
          .where('normalizedFarmOrSite')
          .startsWith(normalizedText)
          .limit(pageSize)
          .toArray(),
      ]
      if (normalizedDigits) {
        searches.push(
          this.database.clients
            .where('normalizedPhone')
            .startsWith(normalizedDigits)
            .limit(pageSize)
            .toArray(),
        )
      }
      const records = (await Promise.all(searches)).flat()
      const unique = new Map(records.map((record) => [record.id, record]))
      return [...unique.values()]
        .filter((record) => includeInactive || record.active)
        .slice(0, pageSize)
        .map((record) => clientSchema.parse(record))
    })
  }

  async findPotentialDuplicate(
    companyName: string,
    phone: string,
    excludeId?: string,
  ): Promise<Client | undefined> {
    return executeDatabaseOperation('verificar possível cliente duplicado', async () => {
      const normalizedName = normalizeSearchText(companyName)
      const normalizedPhone = normalizePhone(phone)
      if (!normalizedName || !normalizedPhone) return undefined
      const candidates = await this.database.clients
        .where('normalizedCompanyName')
        .equals(normalizedName)
        .limit(20)
        .toArray()
      const duplicate = candidates.find(
        (client) => client.id !== excludeId && client.normalizedPhone === normalizedPhone,
      )
      return duplicate ? clientSchema.parse(duplicate) : undefined
    })
  }

  async update(id: string, input: UpdateClientInput): Promise<Client> {
    return executeDatabaseOperation('editar cliente', async () => {
      const data = updateClientInputSchema.parse(input)
      return this.database.transaction(
        'rw',
        [this.database.clients, this.database.auditLogs],
        async () => {
          const existing = clientSchema.parse(
            requireRecord(await this.database.clients.get(id), 'Cliente'),
          )
          const now = new Date().toISOString()
          const client = clientSchema.parse({
            ...existing,
            ...data,
            normalizedCompanyName: normalizeSearchText(data.companyName ?? existing.companyName),
            normalizedFarmOrSite: normalizeSearchText(data.farmOrSite ?? existing.farmOrSite),
            normalizedPhone: normalizePhone(data.phone ?? existing.phone),
            updatedAt: now,
          })
          await this.database.clients.put(client)
          await writeAuditLog(this.database, 'client', id, 'update', changedFields(data), now)
          return client
        },
      )
    })
  }

  async inactivate(id: string): Promise<Client> {
    return executeDatabaseOperation('inativar cliente', async () =>
      this.database.transaction(
        'rw',
        [this.database.clients, this.database.auditLogs],
        async () => {
          const existing = clientSchema.parse(
            requireRecord(await this.database.clients.get(id), 'Cliente'),
          )
          if (!existing.active) return existing
          const now = new Date().toISOString()
          const client = clientSchema.parse({
            ...existing,
            active: false,
            activeKey: toActiveKey(false),
            updatedAt: now,
          })
          await this.database.clients.put(client)
          await writeAuditLog(this.database, 'client', id, 'inactivate', {}, now)
          return client
        },
      ),
    )
  }

  async reactivate(id: string): Promise<Client> {
    return executeDatabaseOperation('reativar cliente', async () =>
      this.database.transaction(
        'rw',
        [this.database.clients, this.database.auditLogs],
        async () => {
          const existing = clientSchema.parse(
            requireRecord(await this.database.clients.get(id), 'Cliente'),
          )
          if (existing.active) return existing
          const now = new Date().toISOString()
          const client = clientSchema.parse({
            ...existing,
            active: true,
            activeKey: toActiveKey(true),
            updatedAt: now,
          })
          await this.database.clients.put(client)
          await writeAuditLog(
            this.database,
            'client',
            id,
            'update',
            { fields: ['active'], from: false, to: true },
            now,
          )
          return client
        },
      ),
    )
  }

  async delete(id: string): Promise<void> {
    return executeDatabaseOperation('apagar cliente', async () =>
      this.database.transaction(
        'rw',
        [
          this.database.clients,
          this.database.machines,
          this.database.inspections,
          this.database.auditLogs,
        ],
        async () => {
          const client = clientSchema.parse(
            requireRecord(await this.database.clients.get(id), 'Cliente'),
          )
          if (client.active) {
            throw new DatabaseOperationError(
              'invalid_state',
              'Cliente ativo não pode ser apagado; inative primeiro.',
            )
          }
          const [machineCount, inspectionCount] = await Promise.all([
            this.database.machines.where('clientId').equals(id).count(),
            this.database.inspections.where('clientId').equals(id).count(),
          ])
          if (machineCount || inspectionCount) {
            throw new DatabaseOperationError(
              'protected_record',
              `Cliente possui ${machineCount} máquinas e ${inspectionCount} inspeções vinculadas.`,
            )
          }
          const now = new Date().toISOString()
          await this.database.clients.delete(id)
          await writeAuditLog(this.database, 'client', id, 'delete', {}, now)
        },
      ),
    )
  }
}
